import { apiBase } from './apiBase';
import { ApiError, apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { TaskStatusCategory } from './taskStatusesApi';

// ---------------------------------------------------------------------------
// Task reports API client — mirrors backend src/routes/taskReports.ts
// (mounted at /api/reports/tasks, gated by tasks.canViewReports).
// ---------------------------------------------------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Shared report filters. Dates are YYYY-MM-DD; lists are joined as CSV. */
export interface ReportFilters {
  from?: string;
  to?: string;
  projectId?: string[];
  assigneeId?: string[];
  departmentId?: string[];
  label?: string[];
}

function buildQuery(filters: ReportFilters, extra?: Record<string, string>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) continue;
    const str = Array.isArray(value) ? value.join(',') : value;
    if (str !== '') params.set(key, str);
  }
  for (const [key, value] of Object.entries(extra ?? {})) {
    if (value !== '') params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Response shapes ---------------------------------------------------------

export interface OverviewReport {
  spentHours: number;
  estimatedHours: number;
  /** Raw estimate/spent ratio over completed estimated tasks (UI clamps). */
  estimateAccuracy: number | null;
  createdCount: number;
  completedCount: number;
  onTimeCount: number;
  onTimeRate: number | null;
  overrunTaskCount: number;
  activePeople: number;
}

export interface HoursReportTask {
  id: string;
  identifier: string | null;
  title: string;
  project: { id: string; name: string; code: string | null } | null;
  assignee: { id: string; name: string } | null;
  status: { slug: string; name: string; category: TaskStatusCategory; color: string };
  estimate: number | null;
  spent: number;
  delta: number | null;
  pct: number | null;
  completedAt: string | null;
}

export interface HoursReport {
  byProject: Array<{
    projectId: string;
    name: string;
    code: string | null;
    estimatedHours: number;
    spentHours: number;
  }>;
  byPerson: Array<{ userId: string; name: string | null; spentHours: number }>;
  tasks: HoursReportTask[];
}

export interface OverrunsReport {
  byPerson: Array<{
    userId: string | null;
    name: string | null;
    taskCount: number;
    totalOverrunHours: number;
  }>;
  tasks: HoursReportTask[];
}

export type ThroughputBucket = 'week' | 'month';

export interface ThroughputReport {
  bucket: ThroughputBucket;
  buckets: Array<{ bucketStart: string; created: number; completed: number }>;
}

export interface CycleTimeProjectRow {
  projectId: string;
  name: string;
  code: string | null;
  count: number;
  avgCycleDays: number | null;
  medianCycleDays: number | null;
  avgLeadDays: number | null;
  medianLeadDays: number | null;
}

export interface CycleTimeReport {
  count: number;
  avgCycleDays: number | null;
  medianCycleDays: number | null;
  avgLeadDays: number | null;
  medianLeadDays: number | null;
  byProject: CycleTimeProjectRow[];
}

export interface WorkloadReport {
  people: Array<{
    userId: string;
    name: string;
    capacity: number;
    openTasks: number;
    openEstimatedHours: number;
    remainingHours: number;
    utilization: number | null;
  }>;
}

export type OverdueBucket = 'd1_7' | 'd7_30' | 'd30plus';

export interface OverdueTaskRow {
  id: string;
  identifier: string | null;
  title: string;
  project: { id: string; name: string; code: string | null } | null;
  assignee: { id: string; name: string } | null;
  status: { slug: string; name: string; category: TaskStatusCategory; color: string };
  estimate: number | null;
  dueDate: string;
  daysOverdue: number;
  bucket: OverdueBucket;
}

export interface OverdueReport {
  buckets: Record<OverdueBucket, number>;
  byPerson: Array<{ userId: string | null; name: string | null; count: number }>;
  byProject: Array<{ projectId: string; name: string; code: string | null; count: number }>;
  tasks: OverdueTaskRow[];
}

// --- Fetchers -----------------------------------------------------------------

async function get<T>(path: string): Promise<T> {
  const res = await apiFetch<Envelope<T>>(path, { headers: authHeaders() });
  return res.data;
}

export function getOverviewReport(filters: ReportFilters): Promise<OverviewReport> {
  return get(`/api/reports/tasks/overview${buildQuery(filters)}`);
}

export function getHoursReport(filters: ReportFilters): Promise<HoursReport> {
  return get(`/api/reports/tasks/hours${buildQuery(filters)}`);
}

export function getOverrunsReport(filters: ReportFilters): Promise<OverrunsReport> {
  return get(`/api/reports/tasks/overruns${buildQuery(filters)}`);
}

export function getThroughputReport(
  filters: ReportFilters,
  bucket: ThroughputBucket,
): Promise<ThroughputReport> {
  return get(`/api/reports/tasks/throughput${buildQuery(filters, { bucket })}`);
}

export function getCycleTimeReport(filters: ReportFilters): Promise<CycleTimeReport> {
  return get(`/api/reports/tasks/cycle-time${buildQuery(filters)}`);
}

export function getWorkloadReport(filters: ReportFilters): Promise<WorkloadReport> {
  return get(`/api/reports/tasks/workload${buildQuery(filters)}`);
}

export function getOverdueReport(filters: ReportFilters): Promise<OverdueReport> {
  return get(`/api/reports/tasks/overdue${buildQuery(filters)}`);
}

/**
 * Download the /hours task table as .xlsx. Streams a binary attachment, so
 * this bypasses apiFetch's JSON handling (same pattern as directoryExcelApi).
 */
export async function downloadHoursExport(filters: ReportFilters): Promise<void> {
  const apiUrl = apiBase();
  const res = await fetch(`${apiUrl}/api/reports/tasks/hours/export${buildQuery(filters)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'Could not export the report.');
  }
  const disposition = res.headers.get('Content-Disposition');
  const match = disposition?.match(/filename="([^"]+)"/);
  const filename = match?.[1] ?? 'raport-ore.xlsx';
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
